'use client';

import { useRouter } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import MenuLink from "./MenuLink";
import useLoginModal from "@/app/hooks/useLoginModal";
import useSignupModal from "@/app/hooks/useSignupModal";
import LogoutButton from "../LogoutButton";
import { useAuthStore } from "@/app/hooks/auth";

interface UserNavProps {
    userId?: string | null;
}

const UserNav: React.FC<UserNavProps> = ({ userId }) => {
    const router = useRouter();
    const login = useLoginModal();
    const signup = useSignupModal();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { user } = useAuthStore();

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        /* Otimizado: flex e itens alinhados com gap controlado para manter o formato do botão pill */
        <div ref={dropdownRef} className="relative inline-block">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 lg:px-4 lg:py-2 flex items-center gap-3 border rounded-full hover:shadow-sm transition bg-white"
            >
                {/* Ícone de Menu (Hambúrguer) */}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-600">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                </svg>

                {/* Ícone de Usuário (Avatar) */}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-gray-600">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                </svg>
            </button>

            {isOpen && (
                /* Otimizado: mt-2 em vez de top fixo para evitar sobreposição caso a altura da navbar mude */
                <div className="w-[220px] absolute mt-2 right-0 bg-white border rounded-xl shadow-md flex flex-col cursor-pointer z-50 overflow-hidden py-1">
                    {userId ? (
                        <>
                            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                                <p className="text-xs text-gray-500 font-medium">Olá, é bom te ver </p>
                                <p className="text-sm font-semibold text-gray-800 truncate">
                                  {user?.name || user?.email || "😃😃😃"}
                                </p>
                            </div>

                            <MenuLink label="Contratos" onClick={() => { setIsOpen(false); router.push('/mycontracts'); }} />
                            <MenuLink label="Meu show" onClick={() => { setIsOpen(false); router.push('/myproducts'); }} />
                            <MenuLink label="Reservas" onClick={() => { setIsOpen(false); router.push('/myreservations'); }} />
                            <MenuLink label="Conta" onClick={() => { setIsOpen(false); router.push('/dashboard'); }} />
                            <MenuLink label="Meus álbuns" onClick={() => { setIsOpen(false); router.push('/album/meus-downloads'); }} />

                            {/* Condicionais protegidas contra renderização parcial síncrona */}
                            {user && (user.is_staff || user.is_superuser) && (
                                <MenuLink label="Check-in" onClick={() => { setIsOpen(false); router.push('/mycheckin'); }} />
                            )}

                            {user && (user.is_staff || user.is_superuser) && (
                                <MenuLink label="Novo ingresso" onClick={() => { setIsOpen(false); router.push('/shop/novo'); }} />
                            )}

                            <hr className="my-1 border-gray-100" />
                            <LogoutButton />
                        </>
                    ) : (
                        <>
                            <div className="px-4 py-2 border-b border-gray-100 bg-amber-50/60">
                                <p className="text-xs text-amber-800">
                                    Faça login para acessar seus contratos e ingressos.
                                </p>
                            </div>

                            <MenuLink label="👤 Entrar" onClick={() => { setIsOpen(false); login.open() }} />
                            <MenuLink label="👥 Registre-se" onClick={() => { setIsOpen(false); signup.open() }} />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default UserNav;
